import { api } from '@/lib/axios-client'
import {
  type GetPresignedDownloadUrlResponse,
  type GetPresignedUploadUrlResponse,
  type GetPresignedUploadUrlRequest,
  type CreateDocumentRequest,
  type CreateDocumentResponse,
} from '../types/attachment-types'
import { sanitizeFileName, uploadToBlob } from '@/helpers/file-upload-helpers'
import { isBlobNameOnly, getBlobName } from '@/utils/file-helper'
import type {
  File as PermitSubmittalFile,
  RequiredDocument as PermitRequiredDocument,
  OtherDocument,
} from '../../permit-checklist/types/permit-submital-types'
import type {
  MappedFile,
  RequiredDocument,
} from '../../permit-checklist/types/types'
import type { Dispatch, SetStateAction } from 'react'
import type { TFunction } from 'i18next'
import { startSentrySpan } from '@/lib/monitoring/sentry-loader'
import {
  trackDocumentUpload,
  trackDocumentUploadFailed,
} from '@/lib/monitoring/analytics'

export const getDownloadPresignedUrl = async (
  documentId: string,
): Promise<GetPresignedDownloadUrlResponse | null> => {
  const data = await api.get<GetPresignedDownloadUrlResponse>(
    `/Attachments/${documentId}/get-presigned-download-url`,
  )
  if (!data) return null
  return data
}

export const createPresignedUploadUrl = async (
  data: GetPresignedUploadUrlRequest,
): Promise<GetPresignedUploadUrlResponse> => {
  return api.post<GetPresignedUploadUrlResponse>(
    '/Attachments/get-presigned-upload-url',
    data,
  )
}

export const createDocument = async (
  data: CreateDocumentRequest,
): Promise<CreateDocumentResponse> => {
  return api.post<CreateDocumentResponse>('/Attachments', data)
}

export async function uploadDocument(
  file: File,
  uploadType: 'required' | 'other' = 'other',
): Promise<PermitSubmittalFile> {
  const sanitizedFileName = sanitizeFileName(file.name)

  return startSentrySpan(
    {
      name: `upload.document`,
      op: 'file.upload',
      attributes: {
        'file.name': sanitizedFileName,
        'file.size': file.size,
        'upload.type': uploadType,
      },
    },
    async () => {
      const startTime = Date.now()
      try {
        const presigned = await createPresignedUploadUrl({
          FileName: sanitizedFileName,
        })
        await uploadToBlob(file, presigned.presignedUrl)
        trackDocumentUpload(
          sanitizedFileName,
          file.size,
          uploadType,
          Date.now() - startTime,
        )

        return {
          name: sanitizedFileName,
          blobName: presigned.blobName,
          type: file.type,
          size: file.size,
          url: presigned.presignedUrl,
        } satisfies PermitSubmittalFile
      } catch (error) {
        trackDocumentUploadFailed(sanitizedFileName, file.size, uploadType)
        throw error
      }
    },
  )
}

export async function uploadRequiredDocuments(
  uploadDocuments: RequiredDocument[],
  requiredDocuments: PermitRequiredDocument[],
  setUploadProgress: Dispatch<SetStateAction<string>>,
  t?: TFunction,
) {
  for (const doc of uploadDocuments) {
    if (doc.files.length > 0) {
      const fileToUpload = doc.files[0].file

      // A placeholder representing an already-uploaded, untouched file
      // (createFileFromBlobName) — reuse its existing blob instead of
      // re-uploading empty stub content under a fresh blob name, which
      // would make an unchanged document look brand-new to the backend
      // and overwrite it with an empty file.
      if (isBlobNameOnly(fileToUpload)) {
        requiredDocuments.push({
          requiredDocumentId: doc.id,
          name: doc.name,
          Document: {
            name: fileToUpload.name,
            blobName: getBlobName(fileToUpload)!,
            type: fileToUpload.type,
            size: fileToUpload.size,
            url: '',
          },
        })
        continue
      }

      setUploadProgress(
        t
          ? t('permitChecklist.upload.uploadingFile', { name: doc.name })
          : `Uploading ${doc.name}…`,
      )
      const uploadedFile = await uploadDocument(fileToUpload, 'required')

      requiredDocuments.push({
        requiredDocumentId: doc.id,
        name: doc.name,
        Document: uploadedFile,
      })
    } else {
      requiredDocuments.push({
        requiredDocumentId: doc.id,
        name: doc.name,
        Document: null,
      })
    }
  }
}

export async function uploadOtherDocuments(
  uploadOtherFiles: MappedFile[],
  otherDocuments: OtherDocument[],
  setUploadProgress: Dispatch<SetStateAction<string>>,
  t?: TFunction,
) {
  for (const mappedFile of uploadOtherFiles) {
    setUploadProgress(
      t
        ? t('permitChecklist.upload.uploadingFile', {
            name: mappedFile.displayName,
          })
        : `Uploading ${mappedFile.displayName}…`,
    )
    const uploadedFile = await uploadDocument(mappedFile.file, 'other')
    otherDocuments.push(uploadedFile)
  }
}
